import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useMachineAuditLogs } from '@/api/audit-logs'
import { useMachine } from '@/api/machines'
import { useWorkspace } from '@/api/workspace'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { MachineBadges } from '@/components/machines/machine-badges'
import { formatWhole, rateMissingReason } from '@/components/machines/machine-format'
import { RecordHistory } from '@/components/record-history/record-history'
import { Button } from '@/components/ui/button'

// Machine detail (UI-SCREENS F-03, #127, #128, #144): the Machine, its rate inputs, the Batch
// printing on it, *Start batch* while it is idle and the Batch history link, which opens F-04
// filtered to this Machine (also when archived; Owner and Staff), and the rate breakdown (08a #202,
// "—" with the reason while an input is missing). The Owner sees the History section: archive and rate input
// changes (#137).
export const Route = createFileRoute('/w/$slug/machines/$machineId/')({
  component: MachineDetail,
  staticData: { breadcrumb: 'machines.detail.title' },
})

function MachineDetail() {
  const { t } = useTranslation()
  const { slug, machineId } = Route.useParams()
  const machine = useMachine(slug, Number(machineId))
  const isOwner = useWorkspace(slug).data?.role === 'owner'
  const history = useMachineAuditLogs(slug, Number(machineId), { enabled: isOwner })

  if (machine.isPending) return <LoadingSkeleton variant="page" />
  if (machine.isError || !machine.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  const data = machine.data
  const rows: [string, string][] = [
    [t('machines.detail.model'), data.model ?? '—'],
    [t('machines.detail.purchasePrice'), formatWhole(data.purchase_price)],
    [t('machines.detail.lifetimeHours'), formatWhole(data.lifetime_hours)],
    [t('machines.detail.watts'), formatWhole(data.watts)],
  ]
  // The rate breakdown (08a #202), whole Rp for display; COGS uses the exact rate.
  const breakdown = data.rate_breakdown
  const rateRows: [string, string][] = breakdown
    ? [
        [t('machines.rateBreakdown.depreciation'), formatWhole(breakdown.depreciation)],
        [t('machines.rateBreakdown.power'), formatWhole(breakdown.power)],
        [t('machines.rateBreakdown.maintenance'), formatWhole(breakdown.maintenance)],
        [t('machines.rateBreakdown.total'), formatWhole(breakdown.total)],
      ]
    : [[t('machines.detail.hourlyRate'), formatWhole(null)]]

  return (
    <section className="flex max-w-3xl flex-col gap-4">
      <Link to="/w/$slug/machines" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('machines.back')}
      </Link>
      <div className="flex flex-col gap-2">
        <h1 className="font-heading text-xl font-semibold">{data.name}</h1>
        <MachineBadges machine={data} />
      </div>
      {data.printing_batch ? (
        <p className="text-sm">
          {t('machines.detail.currentBatch')}:{' '}
          <Link
            to="/w/$slug/batches/$batchId"
            params={{ slug, batchId: String(data.printing_batch.id) }}
            className="font-medium underline underline-offset-4"
          >
            {data.printing_batch.batch_no}
          </Link>
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {!data.printing_batch && !data.archived_at ? (
          <Button asChild className="h-11">
            <Link to="/w/$slug/batches/new" params={{ slug }} search={{ machineId: data.id }}>
              {t('batches.start')}
            </Link>
          </Button>
        ) : null}
        <Button asChild variant="outline" className="h-11">
          <Link to="/w/$slug/batches" params={{ slug }} search={{ machineId: data.id }}>
            {t('machines.detail.batchHistory')}
          </Link>
        </Button>
        {data.can.update ? (
          <Button asChild variant="outline" className="h-11">
            <Link to="/w/$slug/machines/$machineId/edit" params={{ slug, machineId }}>
              {t('machines.edit')}
            </Link>
          </Button>
        ) : null}
      </div>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-[max-content_1fr]">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-sm text-muted-foreground">{label}</dt>
            <dd className="font-medium">{value}</dd>
          </div>
        ))}
      </dl>
      <h2 className="font-heading text-base font-semibold">{t('machines.rateBreakdown.title')}</h2>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-[max-content_1fr]">
        {rateRows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-sm text-muted-foreground">{label}</dt>
            <dd className="font-medium">{value}</dd>
          </div>
        ))}
      </dl>
      {breakdown ? null : <p className="text-sm">{rateMissingReason(t, data.rate_missing)}</p>}
      <p className="text-sm text-muted-foreground">{t('machines.detail.rateHint')}</p>

      {isOwner ? <RecordHistory entries={history.data} isPending={history.isPending} isError={history.isError} /> : null}
    </section>
  )
}
